/**
 * EIP-6963 multi-wallet discovery.
 *
 * Modern browser wallets (MetaMask, Rabby, Coinbase Wallet, Phantom EVM,
 * OKX, Frame, Trust, etc.) announce themselves via the EIP-6963
 * `eip6963:announceProvider` event. We dispatch the inverse request
 * event once at module load, listen for announcements and keep the
 * resulting list in a small in-memory store.
 *
 * Falls back to `window.ethereum` when no EIP-6963-aware wallet
 * answers, so legacy browsers / MetaMask without the upgrade still
 * work.
 */

const providersByUuid = new Map();
const subscribers = new Set();

function emit() {
    const list = Array.from(providersByUuid.values()).sort((a, b) =>
        a.info.name.localeCompare(b.info.name)
    );
    for (const fn of subscribers) {
        try {
            fn(list);
        } catch (err) {
            // Subscriber crash should not break discovery for everyone else.
            console.error("wallet subscriber failed", err);
        }
    }
}

if (typeof window !== "undefined") {
    window.addEventListener("eip6963:announceProvider", (event) => {
        const detail = event?.detail;
        if (!detail || !detail.info || !detail.provider) return;
        providersByUuid.set(detail.info.uuid, detail);
        emit();
    });

    // Dispatch the request immediately + once more on load so wallets
    // that listen late (script injected after our bootstrap) still
    // announce themselves.
    window.dispatchEvent(new Event("eip6963:requestProvider"));
    window.addEventListener("load", () => {
        window.dispatchEvent(new Event("eip6963:requestProvider"));
    });
}

/**
 * Returns the currently known wallet announcements, sorted by name.
 * Each entry has shape `{ info: { uuid, name, icon, rdns }, provider }`.
 */
export function listAnnouncedWallets() {
    return Array.from(providersByUuid.values()).sort((a, b) =>
        a.info.name.localeCompare(b.info.name)
    );
}

/**
 * Subscribes to wallet-announcement updates. Returns an unsubscribe
 * function. The callback fires immediately with the current list so
 * components can render synchronously without a placeholder state.
 */
export function subscribeWallets(callback) {
    subscribers.add(callback);
    callback(listAnnouncedWallets());
    return () => {
        subscribers.delete(callback);
    };
}

/**
 * Resolves an EIP-1193 provider for a given wallet announcement uuid.
 * Falls back to `window.ethereum` when uuid is null / unknown so the
 * existing single-wallet flow keeps working on legacy setups.
 */
export function resolveProvider(uuid) {
    if (uuid) {
        const entry = providersByUuid.get(uuid);
        if (entry) return entry.provider;
    }
    if (typeof window !== "undefined" && window.ethereum) {
        return window.ethereum;
    }
    return null;
}
